import { FormSelectInput } from '@tanium/react-form-select-input';
import { FormRow } from '@tanium/react-form';
import get from 'lodash.get';
import { Field, FieldProps, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@tanium/react-theme-context';
import { Box } from '@tanium/react-box';
import { Stack } from '@tanium/react-stack';
import { getSessionConfig } from 'utils/Config';
import Tooltip from 'components/tooltip/Tooltip';
import { AwsRoleTargetAccess } from 'gql/AwsRoleTargetAccess';
import { TARGET_ACCESS_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';

interface AWSRoleBasedAccessProps {
  awsRoleTargetAccess: AwsRoleTargetAccess[];
}

export const AWSRoleBasedAccess = ({ awsRoleTargetAccess }: AWSRoleBasedAccessProps): React.ReactElement => {
  const config = getSessionConfig();
  const theme = useTheme();
  const isGovCloud = config?.isGovCloud;
  const { t } = useTranslation();

  const formInputIdPrefix = 'create-request-';

  const options = awsRoleTargetAccess.map((access) => {
    return {
      label: access.awsRoleName,
      value: access.id,
      description: access.description,
    };
  });

  return (
    <Box display="inline-flex" mr={1}>
      <Field name={TARGET_ACCESS_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('AWS_ROLE')}
            required
            description={
              <Box>
                <Box>{t('AWS_ROLE_DESCRIPTION')}</Box>
                {isGovCloud && <Box color={theme.colors.warning}>{t('ROLE_BASTION_WARNING')}</Box>}
              </Box>
            }
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
            data-testid={`${formInputIdPrefix}${field.name}`}
          >
            <FormSelectInput
              onChange={(option): void => {
                void form.setFieldValue(field.name, option.value);
              }}
              value={options?.find((option) => option.value === field.value)}
              inputId={`${formInputIdPrefix}${field.name}`}
              options={options}
              aria-label={t('AWS_ROLE')}
              formatOptionLabel={(
                option: {
                  value: string;
                  label: string;
                  description: string;
                },
                state
              ) => {
                const isSelected =
                  state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                /* istanbul ignore next */
                const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                return (
                  <Stack direction="column" itemSpacing={0.5}>
                    <Box>{option.label}</Box>
                    <Tooltip content={option.description}>
                      <Box color={subtextColor} fontSize={11}>
                        {option.description}
                      </Box>
                    </Tooltip>
                  </Stack>
                );
              }}
              styles={{
                singleValue: (base) => ({
                  ...base,
                  marginTop: theme.space[1],
                  marginBottom: theme.space[1],
                  position: 'relative',
                  transform: 'none',
                  whiteSpace: 'normal',
                  maxHeight: '3.75em',
                }),
                input: (base) => ({
                  ...base,
                  position: 'absolute',
                  top: theme.space[1] - 2,
                  paddingTop: 0,
                }),
              }}
            />
          </FormRow>
        )}
      </Field>
    </Box>
  );
};

export default AWSRoleBasedAccess;
